import React, { useState } from 'react';
import ModalComponent from '../../utils/modal';
import ImageModalBodyContent from './images';
import MediaPreview from './mediaPreview';
import { connect } from 'react-redux';
import { validateExternalLink } from '../../../middleware/utils';
import { isValidAudioUrl } from './audios';

interface MediaModalInterface {
  handleAddElement: Function;
  setShow: Function;
  mediaType: string;
  imageCollection: Array<{ id: number; name: string; url: string }>;
  setDecorativeText: Function;
  setDescription?: Function;
  decorativeText: string;
  show: boolean;
  imageType: string;
  handleLocalImageUpload: {
    setLocalImageDetails: Function;
    uploadImage: Function;
  };
  isErrorInFetch: boolean;
  isFetching: boolean;
  componentType?: string;
}

interface MediaModalBodyContentInterface {
  handleConfirmState: Function;
  screenType: string;
  mediaType: string;
  imageCollection: Array<{ id: number; name: string; url: string }>;
  selectedMediaDetails: {
    elementType: string;
    url: string;
    fileName: string;
    name: string;
    mediaType: string;
    autoplay?: boolean;
    isTypedLink?: boolean;
  };
  setSelectedMediaDetails: Function;
  isError: boolean;
  setIsError: Function;
  isAudioUrlError: boolean;
  setIsAudioUrlError: Function;
  setDecorativeText: Function;
  setDescription: Function;
  setScreenType: Function;
  imageType: string;
  setLocalImageDetails: Function;
  isErrorInFetch: boolean;
  isFetching: boolean;
  fileNameDetails: { type?: string; isError?: boolean };
  setFileNameDetails: Function;
  componentType?: string;
}

const MediaModal = (props: MediaModalInterface) => {
  const {
    handleAddElement,
    setShow,
    mediaType,
    imageCollection,
    setDecorativeText,
    setDescription,
    show,
    imageType,
    handleLocalImageUpload,
    isErrorInFetch,
    isFetching,
    decorativeText,
  } = props;
  const { setLocalImageDetails, uploadImage } = handleLocalImageUpload;
  const [confirmButtonState, setConfirmButtonState] = useState(true);
  const [screenType, setScreenType] = useState('media');
  const [selectedMediaDetails, setSelectedMediaDetails] = useState<{
    elementType: string;
    url: string;
    fileName: string;
    name: string;
    mediaType: string;
    autoplay: boolean;
    isTypedLink?: boolean;
  }>({
    elementType: '',
    url: '',
    fileName: '',
    name: '',
    mediaType: '',
    autoplay: false,
  });
  const [isError, setIsError] = useState(false);
  const [isAudioUrlError, setIsAudioUrlError] = useState(false);
  const [fileNameDetails, setFileNameDetails] = useState<{ type?: string; isError?: boolean }>({
    type: '',
    isError: false,
  });

  const getModalBodyContent = () => {
    return (
      <MediaModalBodyContent
        handleConfirmState={setConfirmButtonState}
        mediaType={mediaType}
        screenType={screenType}
        imageCollection={imageCollection}
        selectedMediaDetails={selectedMediaDetails}
        setSelectedMediaDetails={setSelectedMediaDetails}
        isError={isError}
        setIsError={setIsError}
        isAudioUrlError={isAudioUrlError}
        setIsAudioUrlError={setIsAudioUrlError}
        setDecorativeText={setDecorativeText}
        setDescription={setDescription as Function}
        setScreenType={setScreenType}
        setLocalImageDetails={setLocalImageDetails}
        imageType={imageType}
        isErrorInFetch={isErrorInFetch}
        isFetching={isFetching}
        fileNameDetails={fileNameDetails}
        setFileNameDetails={setFileNameDetails}
        componentType={props.componentType}
      />
    );
  };

  const resolveFinalUrl = (
    elementType: string,
    url: string,
    name: string,
    isTypedLink?: boolean,
  ) => {
    if (elementType === 'externalLink') return validateExternalLink(url);
    if (elementType === 'icon') return name;
    if (elementType === 'audio' && isTypedLink) {
      return isValidAudioUrl(url) ? url : { error: 'Invalid audio url' };
    }
    return url;
  };

  const uploadSelectedMedia = (
    fileName: string,
    autoplay: boolean,
    finalUrl: unknown,
    elementType: string,
  ) => {
    if (imageType === 'localImage') {
      uploadImage(fileName.trim());
      return;
    }
    handleAddElement(finalUrl, elementType, autoplay);
  };

  const handleConfirm = () => {
    const { elementType, url, fileName, name, autoplay, isTypedLink } = selectedMediaDetails;
    setConfirmButtonState(true);
    const finalUrl = resolveFinalUrl(elementType, url, name, isTypedLink);

    if (screenType === 'media' && (imageType === '' || elementType === 'video')) {
      if ((finalUrl as { error: string }).error) {
        setIsError(true);
        setIsAudioUrlError(true);
        return;
      }
      setScreenType('preview');
      return;
    }

    if (elementType === 'image' && checkFileName()) return;

    setScreenType('media');
    setShow('', 'close');
    uploadSelectedMedia(fileName, autoplay, finalUrl, elementType);
    setSelectedMediaDetails({
      elementType: '',
      url: '',
      fileName: '',
      name: '',
      mediaType: '',
      autoplay: false,
    });
    setFileErrorDetails('', false);
    setLocalImageDetails('', {});
  };

  const checkFileName = () => {
    const { fileName, elementType } = selectedMediaDetails;
    const isFileNameExits =
      imageType === 'localImage' &&
      imageCollection.some((element) => element.name === fileName.trim());
    const isFileNameMatching = decorativeText.trim() === fileName.trim();

    if (elementType === 'externalLink') {
      return false;
    } else if (isFileNameExits) {
      setFileErrorDetails('alreadyExist', true);
      return true;
    } else if (isFileNameMatching) {
      setFileErrorDetails('fileNameMatching', true);
      return true;
    }
    return false;
  };

  const setFileErrorDetails = (type: string, status: boolean) => {
    setFileNameDetails({
      type: type,
      isError: status,
    });
  };

  const handleAction = (type: string) => {
    if (type === 'cancel') {
      setShow('', 'close');
    }
    setLocalImageDetails('', {});
    setConfirmButtonState(true);
    setIsError(false);
    setIsAudioUrlError(false);
    setScreenType('media');
    setSelectedMediaDetails({
      elementType: '',
      url: '',
      fileName: '',
      name: '',
      mediaType: '',
      autoplay: false,
    });
    setFileErrorDetails('', false);
  };

  return (
    <ModalComponent
      show={show}
      size='xl'
      handleCancel={() => handleAction('cancel')}
      modalDetails={{
        title: mediaType,
        body: getModalBodyContent(),
      }}
      confirmButton={{
        label: screenType === 'media' ? 'Next' : 'Add',
        disabled: confirmButtonState,
        handleConfirm: handleConfirm,
      }}
      cancelButton={{
        label: 'Cancel',
      }}
      backButton={{
        label: 'Previous',
        disabled: screenType === 'media',
        handleBack: () => handleAction('back'),
      }}
    />
  );
};

const MediaModalBodyContent = (props: MediaModalBodyContentInterface) => {
  const {
    handleConfirmState,
    screenType,
    mediaType,
    imageCollection,
    selectedMediaDetails,
    setSelectedMediaDetails,
    isError,
    setIsError,
    isAudioUrlError,
    setIsAudioUrlError,
    setDecorativeText,
    setDescription,
    setScreenType,
    setLocalImageDetails,
    imageType,
    isErrorInFetch,
    isFetching,
    fileNameDetails,
    setFileNameDetails,
  } = props;

  return (
    <>
      {screenType === 'media' && (
        <>
          {mediaType === 'Media' && (
            <ImageModalBodyContent
              handleConfirmState={handleConfirmState}
              handleSelectedElement={setSelectedMediaDetails}
              imageCollection={imageCollection}
              isError={isError}
              setIsError={setIsError}
              isAudioUrlError={isAudioUrlError}
              setIsAudioUrlError={setIsAudioUrlError}
              setScreenType={setScreenType}
              setLocalImageDetails={setLocalImageDetails}
              isErrorInFetch={isErrorInFetch}
              isFetching={isFetching}
              componentType={props.componentType}
            />
          )}
        </>
      )}
      {screenType === 'preview' && (
        <MediaPreview
          selectedMediaDetails={selectedMediaDetails}
          mediaType={mediaType}
          handleConfirmState={handleConfirmState}
          setDecorativeText={setDecorativeText}
          setDescription={setDescription}
          imageType={imageType}
          handleSelectedElement={setSelectedMediaDetails}
          fileNameDetails={fileNameDetails}
          setFileNameDetails={setFileNameDetails}
        />
      )}
    </>
  );
};

const mapStateToProps = (storage: {
  imageStorage: { imageCollection: Array<{ id: number; name: string; url: string }> };
}) => ({
  imageCollection: storage.imageStorage.imageCollection,
});

export default connect(mapStateToProps)(MediaModal);
